<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import ImagesWithStripes from '@/components/newDesign/images-with-stripes.vue';
import EditTitleModal from '../EditTitleModal.vue';

const b = bem('cta-ready-order-module');

export default {
  components: {
    PencilIcon,
    EditTitleModal,
    ImagesWithStripes,
  },
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
    blockIndex: {
      type: Number,
      default: 0,
    },
    companyColors: {
      type: Object,
      default: () => ({
        color1: '',
        color2: '',
        color3: '',
      }),
    },
  },
  emits: ['update'],
  data() {
    return {
      isTitleModalVisible: false,
    };
  },
  computed: {
    content() {
      if (this.moduleData?.parsedContent) {
        return this.moduleData.parsedContent;
      }
      if (!this.moduleData?.content) return {};
      if (typeof this.moduleData.content === 'string') {
        try {
          return JSON.parse(this.moduleData.content);
        } catch {
          return {};
        }
      }
      return this.moduleData.content;
    },
    displayTitle() {
      if (this.isMobile && this.content.titleMobile) {
        return this.content.titleMobile;
      }
      return this.content.title || 'Section Title';
    },
    // Convert flat data to stripes format for ImagesWithStripes
    leftStripes() {
      return [
        {
          id: 1,
          text: '',
          color: '#fff',
          colorText: '#242424',
        },
        {
          id: 2,
          text: this.content.leftButtonText || 'BUTTON #1 TEXT',
          textMob: this.content.leftButtonTextMobile || this.content.leftButtonText || 'BUTTON #1 TEXT',
          color: this.buttonBackgroundColor,
          colorText: '#fff',
          link: this.content.leftButtonLink || '',
        },
      ];
    },
    rightStripes() {
      return [
        {
          id: 1,
          text: '',
          color: '#fff',
          colorText: '#242424',
        },
        {
          id: 2,
          text: this.content.rightButtonText || 'BUTTON #2 TEXT',
          textMob: this.content.rightButtonTextMobile || this.content.rightButtonText || 'BUTTON #2 TEXT',
          color: this.buttonBackgroundColor,
          colorText: '#fff',
          link: this.content.rightButtonLink || '',
        },
      ];
    },
    isEvenBlock() {
      return this.blockIndex % 2 === 1;
    },
    buttonBackgroundColor() {
      if (!this.isEditMode && this.companyColors.color2) {
        return this.companyColors.color2;
      }
      return '#242424';
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    openTitleModal() {
      if (this.isEditMode) {
        this.isTitleModalVisible = true;
      }
    },
    handleTitleSave(data) {
      this.updateContent({
        ...this.content,
        title: data.title,
        titleMobile: data.titleMobile,
      });
      this.isTitleModalVisible = false;
    },
    // Handle updates from ImagesWithStripes component
    handleImagesUpdate(updates) {
      const newContent = { ...this.content };

      // Handle image updates
      if ('leftImage' in updates) newContent.leftImage = updates.leftImage;
      if ('leftImageMobile' in updates) newContent.leftImageMobile = updates.leftImageMobile;
      if ('rightImage' in updates) newContent.rightImage = updates.rightImage;
      if ('rightImageMobile' in updates) newContent.rightImageMobile = updates.rightImageMobile;

      // Handle stripe updates - convert back to flat format
      if (updates.leftStripes) {
        const stripe = updates.leftStripes[1]; // Second stripe has text/link
        if (stripe) {
          newContent.leftButtonText = stripe.text;
          newContent.leftButtonTextMobile = stripe.textMob;
          newContent.leftButtonLink = stripe.link;
        }
      }
      if (updates.rightStripes) {
        const stripe = updates.rightStripes[1]; // Second stripe has text/link
        if (stripe) {
          newContent.rightButtonText = stripe.text;
          newContent.rightButtonTextMobile = stripe.textMob;
          newContent.rightButtonLink = stripe.link;
        }
      }

      this.updateContent(newContent);
    },
    updateContent(newContent) {
      const contentWithBackground = {
        ...newContent,
        backgroundColor: this.isEvenBlock
          ? (this.companyColors.color3 || 'transparent')
          : 'transparent',
        backgroundButtonColor: this.companyColors.color2 || '#242424',
      };
      this.$emit('update', {
        ...this.moduleData,
        content: JSON.stringify(contentWithBackground),
      });
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, popup: isPopup })">
    <div
      :class="b('title-row', { editable: isEditMode })"
      role="button"
      tabindex="0"
      @click="openTitleModal"
      @keydown.enter="openTitleModal"
    >
      <h4 :class="b('title', { mobile: isMobile, popup: isPopup })">{{ displayTitle }}</h4>
      <PencilIcon v-if="isEditMode" :size="20" color="#fff" :class="b('edit-icon')" />
    </div>

    <ImagesWithStripes
      :class="b('images-list', { mobile: isMobile, popup: isPopup })"
      :left-image="content.leftImage"
      :left-image-mobile="content.leftImageMobile"
      :right-image="content.rightImage"
      :right-image-mobile="content.rightImageMobile"
      :left-stripes="leftStripes"
      :right-stripes="rightStripes"
      :is-pod="true"
      :is-mobile-view="isMobile"
      :is-edit-mode="isEditMode"
      @update="handleImagesUpdate"
    />

    <EditTitleModal
      :visible="isTitleModalVisible"
      :title="content.title"
      :title-mobile="content.titleMobile"
      @close="isTitleModalVisible = false"
      @save="handleTitleSave"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.cta-ready-order-module {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #9E9E9E;
  padding: 64px 0;

  @include mediaMinMax(1025, $normal) {
    padding: vw-full(64) 0;
  }

  &_popup {
    width: 1200px;
    padding: 20px 0;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(20) 0;
    }
  }
  &_mobile {
    width: 412px;
    padding: 40px 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
    }

    &_editable {
      cursor: pointer;
      padding: 10px 20px;
      margin: -10px -20px;
      border-radius: 8px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__title {
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 40px;
    font-weight: 600;
    margin: 0;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }

    &_popup,
    &_mobile {
      font-size: 28px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(28);
      }
    }
  }

  &__edit-icon {
    flex-shrink: 0;
  }

  &__images-list {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 40px;
    margin-top: 32px;
    position: relative;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(40);
      margin-top: vw-full(32);
    }

    &_popup {
      gap: 33px;
      margin-top: 20px;
      padding-bottom: 30px;

      @include mediaMinMax(1025, $normal) {
        gap: vw-full(33);
        margin-top: vw-full(20);
        padding-bottom: vw-full(30);
      }
    }
    &_mobile {
      flex-direction: column;
      gap: 56px;
      margin-top: 28px;
      padding-bottom: 0;
    }
  }

  &__image-item {
    width: 620px;
    height: 348px;
    border-radius: 36px;
    background: #d9d9d9;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(620);
      height: vw-full(348);
      border-radius: vw-full(36);
    }

    &_popup {
      width: 504px;
      height: 283px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(504);
        height: vw-full(283);
      }
    }

    &_editable:hover {
      background: #c5c5c5;
    }
    &_mobile {
      width: 412px;
      height: auto;
      aspect-ratio: 620 / 348;
      border-radius: 20px 0;
    }
  }

  &__placeholder-text {
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 400;
    color: #7f7f7f;
    text-align: center;
    line-height: normal;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
  }

  &__placeholder-plus {
    font-size: 56px;
    line-height: 1;
    font-weight: 200;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(56);
    }
  }

  &__stripe {
    border: none;
    outline: none;
    position: absolute;
    bottom: 0;
    display: flex;
    align-items: center;
    font-family: Montserrat, sans-serif;
    font-size: 22px;
    font-weight: 600;
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(22);
    }
    &_mobile {
      font-size: vw-full(20);
    }
    &_first {
      height: 40px;
      width: calc(50% - 208px);
      bottom: 48px;
      z-index: 1;
      border-radius: 40px;
      background-color: #fff !important;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(40);
        width: calc(50% - #{vw-full(208)});
        bottom: vw-full(48);
        border-radius: vw-full(40);
      }

      &_popup {
        height: 33px;

        @include mediaMinMax(1025, $normal) {
          height: vw-full(33);
        }
      }
    }
    &_second {
      height: 64px;
      width: calc(50% - 153px);
      bottom: 0;
      z-index: 2;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(64);
        width: calc(50% - #{vw-full(153)});
        bottom: vw-full(0);
      }

      &::after,
      &::before {
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        width: 14px;
        height: 14px;
        border: solid currentColor;
        border-width: 0 3px 3px 0;
        border-color: #fff;
        padding: 3px;
        background: transparent;
        pointer-events: none;
        box-sizing: border-box;

        @include mediaMinMax(1025, $normal) {
          width: vw-full(14);
          height: vw-full(14);
          padding: vw-full(3);
          border-width: 0 vw-full(3) vw-full(3) 0;
        }
      }

      &::after {
        transform: translateY(-50%) rotate(-45deg);
      }

      &::before {
        transform: translateY(-50%) translateX(12px) rotate(-45deg);

        @include mediaMinMax(1025, $normal) {
          transform: translateY(-50%) translateX(#{vw-full(12)}) rotate(-45deg);
        }
      }
    }
    &_left {
      left: 0;
      border-radius: 0 36px 36px 0;
      justify-content: flex-end;
      padding-right: 168px;

      @include mediaMinMax(1025, $normal) {
        border-radius: 0 vw-full(36) vw-full(36) 0;
        padding-right: vw-full(168);
      }

      &::after,
      &::before {
        right: 120px;

        @include mediaMinMax(1025, $normal) {
          right: vw-full(120);
        }
      }
    }
    &_right {
      right: 0;
      border-radius: 36px 0 0 36px;
      justify-content: flex-start;
      padding-left: 168px;

      @include mediaMinMax(1025, $normal) {
        border-radius: vw-full(36) 0 0 vw-full(36);
        padding-left: vw-full(168);
      }

      &::after,
      &::before {
        left: 120px;

        @include mediaMinMax(1025, $normal) {
          left: vw-full(120);
        }
      }

      &::after {
        transform: translateY(-50%) rotate(135deg);
      }

      &::before {
        transform: translateY(-50%) translateX(12px) rotate(135deg);

        @include mediaMinMax(1025, $normal) {
          transform: translateY(-50%) translateX(#{vw-full(12)}) rotate(135deg);
        }
      }
    }
    &_editable {
      &:hover {
        opacity: 0.9;
      }
    }
    &_popup {
      font-size: 17px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(17);
      }
    }
    &_popup#{&}_first {
      height: 33px;
      width: calc(50% - 316px);
      bottom: 46px;
      border-radius: 33px;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(33);
        bottom: vw-full(46);
        width: calc(50% - vw-full(316));
        border-radius: vw-full(33);
      }
    }
    &_popup#{&}_second {
      height: 52px;
      width: calc(50% - 128px);
      bottom: 6px;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(52);
        bottom: vw-full(6);
        width: calc(50% - vw-full(128));
      }
    }
    &_popup#{&}_left {
      border-radius: 0 30px 30px 0;
      padding-right: 150px;

      @include mediaMinMax(1025, $normal) {
        border-radius: 0 vw-full(30) vw-full(30) 0;
        padding-right: vw-full(150);
      }
    }
    &_popup#{&}_right {
      border-radius: 30px 0 0 30px;
      padding-left: 150px;

      @include mediaMinMax(1025, $normal) {
        border-radius: vw-full(30) 0 0 vw-full(30);
        padding-left: vw-full(150);
      }
    }
    &_mobile#{&}_first {
      height: 40px;
      bottom: 20px;
      width: 206px;
    }
    &_mobile#{&}_second {
      height: 64px;
      bottom: -8px;
      width: calc(100% - 153px);

      &::after,
      &::before {
        width: 10px;
        height: 10px;
      }
    }
    &_mobile#{&}_left {
      top: 213px;
      border-radius: 0 26px 26px 0;
      padding-right: 60px;

      &::after,
      &::before {
        right: 40px;
      }
    }
    &_mobile#{&}_left#{&}_first {
      top: 184px;
    }
    &_mobile#{&}_right {
      top: 213px;
      border-radius: 26px 0 0 26px;
      padding-left: 60px;

      &::after,
      &::before {
        left: 40px;
      }
    }
    &_mobile#{&}_right#{&}_first {
      top: initial;
      bottom: 20px;
    }
    &_mobile#{&}_right#{&}_second {
      top: initial;
      bottom: -32px;
    }
  }

  &__stripe-text {
    margin-right: 8px;
    color: #fff;

    @include mediaMinMax(1025, $normal) {
      margin-right: vw-full(8);
    }
  }

  &__stripe-edit-icon {
    flex-shrink: 0;
  }
}
</style>
